import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
} from "@lemonadetraining/web-ui";
import { IconSearch } from "@tabler/icons-react";
import {
  MOVEMENT_REGISTRY_CATEGORY,
  type MovementRegistryCategory,
  type MovementRegistryEntry,
} from "@lemonadetraining/contracts";
import { filterMovements } from "./movementSearchFilter";

const RECENT_KEY = "lt:movement-search:recent";
const MAX_RECENT = 5;

const CATEGORY_LABELS: Record<MovementRegistryCategory | "all", string> = {
  all: "All",
  [MOVEMENT_REGISTRY_CATEGORY.STRENGTH]: "Strength",
  [MOVEMENT_REGISTRY_CATEGORY.OLYMPIC]: "Olympic",
  [MOVEMENT_REGISTRY_CATEGORY.GYMNASTICS]: "Gymnastics",
  [MOVEMENT_REGISTRY_CATEGORY.ENDURANCE]: "Endurance",
  [MOVEMENT_REGISTRY_CATEGORY.WORK_CAPACITY]: "Work Capacity",
};

const CATEGORIES: ReadonlyArray<MovementRegistryCategory | "all"> = [
  "all",
  MOVEMENT_REGISTRY_CATEGORY.STRENGTH,
  MOVEMENT_REGISTRY_CATEGORY.OLYMPIC,
  MOVEMENT_REGISTRY_CATEGORY.GYMNASTICS,
  MOVEMENT_REGISTRY_CATEGORY.ENDURANCE,
  MOVEMENT_REGISTRY_CATEGORY.WORK_CAPACITY,
];

export interface MovementSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  movements: ReadonlyArray<MovementRegistryEntry>;
  /** Override navigate for tests; defaults to react-router. */
  onSelect?: (movementId: string) => void;
}

function readRecent(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is string => typeof s === "string").slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function writeRecent(ids: string[]): void {
  try {
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(ids.slice(0, MAX_RECENT)));
  } catch {
    /* ignore quota / disabled storage */
  }
}

const MovementSearch: React.FC<MovementSearchProps> = ({
  open,
  onOpenChange,
  movements,
  onSelect,
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<MovementRegistryCategory | "all">("all");
  const [activeIndex, setActiveIndex] = useState(0);
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setCategory("all");
      setActiveIndex(0);
      setRecentIds(readRecent());
      // Wait a tick for the dialog to mount.
      const t = setTimeout(() => inputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  const filtered = useMemo(
    () => filterMovements(movements, { query, category }),
    [movements, query, category]
  );

  const recentMovements = useMemo(() => {
    if (query.trim().length > 0) return [];
    const byId = new Map(filterMovements(movements, { query: "", category }).map((m) => [m.id, m]));
    return recentIds
      .map((id) => byId.get(id))
      .filter((m): m is MovementRegistryEntry => Boolean(m));
  }, [movements, recentIds, query, category]);

  const browseList = useMemo(() => {
    if (recentMovements.length === 0) return filtered;
    const recentIdSet = new Set(recentMovements.map((m) => m.id));
    return filtered.filter((m) => !recentIdSet.has(m.id));
  }, [filtered, recentMovements]);

  const visible = useMemo(() => [...recentMovements, ...browseList], [recentMovements, browseList]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, category]);

  const handleSelect = useCallback(
    (id: string) => {
      const next = [id, ...recentIds.filter((r) => r !== id)].slice(0, MAX_RECENT);
      writeRecent(next);
      if (onSelect) {
        onSelect(id);
      } else {
        navigate(`/dashboard/movements/${id}`);
      }
      onOpenChange(false);
    },
    [navigate, onOpenChange, onSelect, recentIds]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(visible.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = visible[activeIndex];
      if (target) handleSelect(target.id);
    }
  };

  const showingRecent = recentMovements.length > 0;
  const showingBrowse = browseList.length > 0;

  const renderOption = (m: MovementRegistryEntry, i: number) => (
    <li
      key={m.id}
      id={`movement-option-${m.id}`}
      role="option"
      aria-selected={i === activeIndex}
      data-testid={`movement-option-${m.id}`}
    >
      <button
        type="button"
        onClick={() => handleSelect(m.id)}
        onMouseEnter={() => setActiveIndex(i)}
        data-testid={`movement-option-button-${m.id}`}
        className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted ${
          i === activeIndex ? "bg-muted" : ""
        }`}
      >
        <span className="font-medium">{m.name}</span>
        <Badge variant="outline" className="text-[10px] uppercase">
          {CATEGORY_LABELS[m.category]}
        </Badge>
      </button>
    </li>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="movement-search-dialog"
        className="sm:max-w-lg p-0 gap-0"
        showCloseButton={false}
        aria-describedby="movement-search-description"
      >
        <DialogTitle className="sr-only">Search movements</DialogTitle>
        <DialogDescription id="movement-search-description" className="sr-only">
          Type to filter the movement library. Use arrow keys to navigate, Enter to select.
        </DialogDescription>

        <div className="flex items-center gap-2 border-b px-3 py-2">
          <IconSearch size={16} className="text-muted-foreground" aria-hidden="true" />
          <Input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search movements…"
            data-testid="movement-search-input"
            aria-label="Search movements"
            aria-controls="movement-search-list"
            aria-activedescendant={
              visible[activeIndex] ? `movement-option-${visible[activeIndex].id}` : undefined
            }
            className="border-0 shadow-none focus-visible:ring-0"
          />
        </div>

        <div className="flex flex-wrap gap-1 border-b px-3 py-2" data-testid="category-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              data-testid={`category-${c}`}
              aria-pressed={category === c}
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition ${
                category === c
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {CATEGORY_LABELS[c]}
            </button>
          ))}
        </div>

        <div
          id="movement-search-list"
          role="listbox"
          aria-label="Search results"
          data-testid="movement-search-list"
          className="max-h-80 overflow-y-auto"
        >
          <p className="sr-only" aria-live="polite" data-testid="movement-search-count">
            {showingRecent
              ? `${recentMovements.length} recent, ${browseList.length} other matches`
              : `${browseList.length} matches`}
          </p>
          {visible.length === 0 ? (
            <p
              className="p-6 text-center text-sm text-muted-foreground"
              data-testid="movement-search-empty"
            >
              No movements match.
            </p>
          ) : (
            <>
              {showingRecent ? (
                <>
                  <p
                    className="px-3 pt-2 text-xs uppercase tracking-wide text-muted-foreground"
                    data-testid="recent-section-header"
                  >
                    Recent
                  </p>
                  <ul className="py-1">{recentMovements.map((m, i) => renderOption(m, i))}</ul>
                </>
              ) : null}
              {showingBrowse ? (
                <>
                  {showingRecent ? (
                    <p
                      className="px-3 pt-2 text-xs uppercase tracking-wide text-muted-foreground"
                      data-testid="browse-section-header"
                    >
                      All movements
                    </p>
                  ) : null}
                  <ul className="py-1">
                    {/* Browse indices continue after Recent so keyboard nav uses one flat activeIndex. */}
                    {browseList.map((m, i) => renderOption(m, i + recentMovements.length))}
                  </ul>
                </>
              ) : null}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MovementSearch;
